import { useState, MouseEvent } from 'react'
import {
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Chip,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import type useUserPage from './useUserPage'
import { capitalize, copyValue } from '@utils'

type UseUserPage = ReturnType<typeof useUserPage>
type UserType = NonNullable<UseUserPage['user']>

const planColour: Record<string, string> = {
    PRO: '#FBBF24',
    BASIC: '#3B82F6',
    STANDARD: '#10B981',
    DEMO: '#6B7280',
    FREE: '#8B5CF6',
}

interface UserHeaderProps {
    user: UserType
    onEdit: () => void
    onDelete: UseUserPage['onDelete']
}

export default function UserHeader({
    user,
    onEdit,
    onDelete,
}: UserHeaderProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const licenceTier = user.licenceTier ?? ''

    const handleOpen = (e: MouseEvent<HTMLElement>) =>
        setAnchorEl(e.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const bgColor = planColour[licenceTier] ?? undefined

    if (!user) return null

    return (
        <Box mb={4}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h1">{user.accountProfile.name}</Typography>

                <IconButton onClick={handleOpen}>
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            onEdit()
                            handleClose()
                        }}
                    >
                        Edit User Information
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            onDelete()
                            handleClose()
                        }}
                    >
                        Delete User
                    </MenuItem>
                </Menu>
            </Box>

            <Box display="flex" alignItems="center" mt={1} mb={0.5}>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mr: 1 }}
                >
                    {user.accountProfile.email}
                </Typography>
                <Tooltip title="Copy email">
                    <IconButton
                        size="small"
                        onClick={copyValue(user.accountProfile.email)}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box display="flex" alignItems="center" mb={1}>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mr: 1 }}
                >
                    {user.accountProfile.id}
                </Typography>
                <Tooltip title="Copy ID">
                    <IconButton
                        size="small"
                        onClick={copyValue(user.accountProfile.id)}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {user.adminProfile?.role && (
                <Box display="flex" alignItems="center" mb={1}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mr: 1 }}
                    >
                        Role: {capitalize(user.adminProfile.role)}
                    </Typography>
                </Box>
            )}

            {user.adminProfile?.org_id && (
                <Box display="flex" alignItems="center" mb={1}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mr: 1 }}
                    >
                        Org ID: {user.adminProfile.org_id}
                    </Typography>
                    <Tooltip title="Copy Org ID">
                        <IconButton
                            size="small"
                            onClick={copyValue(user.adminProfile.org_id)}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Go to Org Page">
                        <IconButton
                            size="small"
                            href={`/organizations/${user.adminProfile.org_id}`}
                            target="_blank"
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {user.adminProfile?.group && (
                <Box display="flex" alignItems="center" mb={1}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mr: 1 }}
                    >
                        Group: {user.adminProfile.group.name}
                    </Typography>
                    <Tooltip
                        title={`Copy Group ID: ${user.adminProfile.group.id}`}
                    >
                        <IconButton
                            size="small"
                            onClick={copyValue(user.adminProfile.group.id)}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Go to Group Page">
                        <IconButton
                            size="small"
                            href={`/group/${user.adminProfile.group.id}`}
                            target="_blank"
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {licenceTier && (
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                        Messenger Pigeon Plan:
                    </Typography>
                    <Chip
                        label={licenceTier}
                        size="small"
                        sx={{
                            ml: 1,
                            textTransform: 'uppercase',
                            backgroundColor: bgColor,
                            color: '#fff',
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}
